// ================================
// IMPORTS
// ================================
import { ref, type Ref } from "vue"
import { useAuthStore } from "@core/stores/auth.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useFetchWrapper } from "@core/composables"
import type { CompanyUser } from "@core/services/user.service"
import type { Building } from "@types"

// ================================
// TYPES & INTERFACES
// ================================

/**
 * Bâtiment formaté pour le dialog (id court pour le template)
 */
export type BuildingFormatted = {
    _id: string
    name: string
    address?: string
    icon?: string
    visible_to?: Array<{ _id_user: string }>
}

/**
 * Retour du composable useAccessBuildingByUserDialog
 */
export type UseAccessBuildingByUserDialogReturn = {
    /** Utilisateur pour lequel gérer l'accès aux bâtiments */
    userToShareBuilding: Ref<CompanyUser | null>
    /** Visibilité du dialog */
    dialogVisible: Ref<boolean>
    /** Liste des bâtiments formatés pour le dialog */
    buildings: Ref<BuildingFormatted[]>
    /** Indique si le chargement des bâtiments est en cours */
    isFetchingBuildings: Ref<boolean>
    /** Ouvre le dialog pour un membre donné */
    openDialog: (member: CompanyUser) => void
    /** Met à jour l'accès d'un utilisateur à un bâtiment (mise à jour locale) */
    onUpdateBuildingAccess: (payload: {
        access: boolean
        buildingId: string
    }) => void
    /** Charge les bâtiments (appelé à l'ouverture du dialog ; exposé pour rafraîchissement manuel si besoin) */
    fetchBuildings: () => Promise<void>
}

// ================================
// COMPOSABLE
// ================================

/**
 * Composable pour gérer la logique métier du dialog d'accès aux bâtiments par utilisateur.
 * Gère l'utilisateur sélectionné, le chargement des bâtiments et les mises à jour locales d'accès.
 */
export function useAccessBuildingByUserDialog(): UseAccessBuildingByUserDialogReturn {
    const authStore = useAuthStore()
    const buildingStore = useBuildingStore()

    const userToShareBuilding = ref<CompanyUser | null>(null)
    const dialogVisible = ref(false)
    const buildings = ref<BuildingFormatted[]>([])

    const { fetch: fetchBuildings, isFetching: isFetchingBuildings } =
        useFetchWrapper(async () => {
            if (!authStore.companyId) {
                return
            }
            const buildingsData = await buildingStore.fetchBuildingsByCompanyId(
                authStore.companyId
            )
            buildings.value = buildingsData.map((building: Building) => ({
                _id: building._id_building,
                name: building.name,
                address: building.address?.inline_name,
                icon: building.infos?.niveau,
                visible_to: building.visible_to || [],
            }))
        })

    function openDialog(member: CompanyUser) {
        userToShareBuilding.value = member
        dialogVisible.value = true
        fetchBuildings()
    }

    function onUpdateBuildingAccess({
        access,
        buildingId,
    }: {
        access: boolean
        buildingId: string
    }) {
        if (!userToShareBuilding.value) return
        const buildingIndex = buildings.value.findIndex(
            (b) => b._id === buildingId
        )
        if (buildingIndex === -1) return
        const building = buildings.value[buildingIndex]
        const newBuilding = { ...building }
        newBuilding.visible_to = newBuilding.visible_to || []
        if (access) {
            if (
                !newBuilding.visible_to.find(
                    (v) => v._id_user === userToShareBuilding.value!._id
                )
            ) {
                newBuilding.visible_to.push({
                    _id_user: userToShareBuilding.value._id,
                })
            }
        } else {
            const index = newBuilding.visible_to.findIndex(
                (v) => v._id_user === userToShareBuilding.value!._id
            )
            if (index !== -1) {
                newBuilding.visible_to.splice(index, 1)
            }
        }
        buildings.value.splice(buildingIndex, 1, newBuilding)
    }

    return {
        userToShareBuilding,
        dialogVisible,
        buildings,
        isFetchingBuildings,
        openDialog,
        onUpdateBuildingAccess,
        fetchBuildings,
    }
}
